import React, { useEffect, useState } from 'react';
import {
  ListBase, ListView, ListContextProvider, FunctionField, TextInput, SelectInput,
  useTranslate, useNotify, useRefresh, useListContext, usePermissions, type ListControllerResult,
} from 'react-admin';
import {
  Alert, Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Stack, TextField as MuiTextField, Tooltip, Typography,
  useMediaQuery, useTheme,
} from '@mui/material';
import {
  ArrowDownwardRounded as DecreaseIcon, ArrowUpwardRounded as IncreaseIcon, SaveOutlined as SaveIcon,
  EditRounded as EditIcon, DeleteOutlineRounded as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useImportFlow } from '@/components/common/ImportDialog';
import {
  ExportFormatDialog, FilterToggle, HelpDialog, ListScreen, PageTitle, ResponsiveDatagrid,
  RowActionsMenu, SectionHeader, useActiveFilterCount, type ExportFormat,
} from '@/components/shell';
import { httpClient } from '@/utils/httpClient';
import { downloadFile, type FileFormat } from '@/utils/jsonFileUtils';
import { USER_ROLES } from '@/config/constants';

interface KycFieldOption {
  id: string;
  name: string;
}

interface DefaultMappingRecord {
  id: string;
  standardFieldName: string;
  priority: number;
  kycFields?: KycFieldOption;
}

type DefaultMappingItem = { standardFieldName: string; kycFieldName: string; priority: number };

const SOURCES = ['MRZ', 'NORM', 'VIZ', 'QR'] as const;

const parseFieldName = (name: string) => {
  const parts = name.split('_');
  const source = parts[0];
  if (source === 'NORM' && parts.length >= 4) {
    return { source, side: parts[1], engine: parts[2], field: parts.slice(3).join('_') };
  }
  return { source, side: null, engine: null, field: parts.slice(1).join('_') };
};

const isQrField = (record: DefaultMappingRecord) => (record?.kycFields?.name || '').startsWith('QR_');

/** An identifier the operator types or reads back verbatim. */
const Mono: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const theme = useTheme();
  return (
    <Typography component="span" variant="body2" sx={{ fontFamily: theme.custom.text.mono }}>
      {children}
    </Typography>
  );
};

/** The KYC field's own name, then where it is read from (source · side · engine) as a quiet second line. */
const KycFieldCell: React.FC<{ record: DefaultMappingRecord }> = ({ record }) => {
  const name = record?.kycFields?.name || '';
  if (!name) return <Typography variant="body2" color="text.secondary" component="span">—</Typography>;
  const parsed = parseFieldName(name);
  const origin = [parsed.source, parsed.side, parsed.engine !== parsed.source ? parsed.engine : null]
    .filter(Boolean)
    .join(' · ');
  return (
    <Box component="span" sx={{ display: 'block' }}>
      <Typography variant="body2" component="span" sx={{ display: 'block' }}>{parsed.field}</Typography>
      <Typography variant="body2" color="text.secondary" component="span" sx={{ display: 'block' }}>{origin}</Typography>
    </Box>
  );
};

/* ── Inline priority editor ── */
const InlinePriority: React.FC<{ record: DefaultMappingRecord }> = ({ record }) => {
  const [value, setValue] = useState<number>(record?.priority ?? 0);
  const [dirty, setDirty] = useState(false);
  const notify = useNotify();
  const refresh = useRefresh();
  const t = useTranslate();

  const change = (next: number) => { setValue(Math.max(0, next)); setDirty(true); };

  const save = async () => {
    try {
      await httpClient.put('/admin/default-mapping/UpdateMappingPriorities', [{ id: record.id, priority: value }]);
      setDirty(false);
      notify('screens.defaultMappings.priorityUpdated', { type: 'success' });
      refresh();
    } catch {
      notify('screens.defaultMappings.priorityFailed', { type: 'error' });
    }
  };

  return (
    <Stack direction="row" alignItems="center" spacing={0.5} onClick={(e) => e.stopPropagation()}>
      <Tooltip title={t('screens.defaultMappings.priorityDecrease')}>
        <span>
          <IconButton
            size="small"
            disabled={value <= 0}
            aria-label={t('screens.defaultMappings.priorityDecrease')}
            onClick={() => change(value - 1)}
          >
            <DecreaseIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <MuiTextField
        value={value}
        onChange={(e) => change(parseInt(e.target.value, 10) || 0)}
        size="small"
        type="number"
        inputProps={{ min: 0, 'aria-label': t('resources.default-mapping.fields.priority') }}
        sx={{ width: 72 }}
      />
      <Tooltip title={t('screens.defaultMappings.priorityIncrease')}>
        <IconButton
          size="small"
          aria-label={t('screens.defaultMappings.priorityIncrease')}
          onClick={() => change(value + 1)}
        >
          <IncreaseIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {dirty && (
        <Tooltip title={t('screens.defaultMappings.prioritySave')}>
          <IconButton size="small" color="primary" aria-label={t('screens.defaultMappings.prioritySave')} onClick={save}>
            <SaveIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
};

/* ── Row actions: edit in a dialog, or delete after confirming ── */
const DefaultMappingActions: React.FC<{
  record: DefaultMappingRecord;
  kycFields: KycFieldOption[];
  hidePriority?: boolean;
}> = ({ record, kycFields, hidePriority }) => {
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [standardFieldName, setStandardFieldName] = useState('');
  const [kycFieldId, setKycFieldId] = useState<string | null>(null);
  const [priority, setPriority] = useState(0);
  const [saving, setSaving] = useState(false);
  const notify = useNotify();
  const refresh = useRefresh();
  const t = useTranslate();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));

  const openEdit = () => {
    setStandardFieldName(record?.standardFieldName || '');
    setKycFieldId(record?.kycFields?.id || null);
    setPriority(record?.priority ?? 0);
    setEditOpen(true);
  };

  const handleSave = async () => {
    if (!standardFieldName.trim() || !kycFieldId) return;
    setSaving(true);
    try {
      await httpClient.put('/admin/default-mapping/UpdateDefaultMapping', {
        id: record.id,
        standardFieldName: standardFieldName.trim(),
        kycFieldId,
        priority,
      });
      notify(t('ra.notification.updated', { smart_count: 1 }));
      refresh();
      setEditOpen(false);
    } catch { notify(t('ra.notification.http_error'), { type: 'error' }); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await httpClient.delete(`/admin/default-mapping/DeleteDefaultMapping/${record.id}`);
      notify(t('ra.notification.deleted', { smart_count: 1 }));
      refresh();
      setDeleteOpen(false);
    } catch { notify(t('ra.notification.http_error'), { type: 'error' }); }
    finally { setSaving(false); }
  };

  return (
    <>
      <RowActionsMenu
        actions={[
          { key: 'edit', label: t('ra.action.edit'), icon: <EditIcon fontSize="small" />, onClick: openEdit },
          {
            key: 'delete',
            label: t('ra.action.delete'),
            icon: <DeleteIcon fontSize="small" />,
            color: 'error',
            dividerBefore: true,
            onClick: () => setDeleteOpen(true),
          },
        ]}
      />

      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isPhone}
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle>{t('shell.form.edit', { name: t('resources.default-mapping.name', { smart_count: 1 }).toLocaleLowerCase() })}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <MuiTextField
              label={t('resources.default-mapping.fields.standardFieldName')}
              value={standardFieldName}
              onChange={(e) => setStandardFieldName(e.target.value)}
              fullWidth
              InputProps={{ sx: { fontFamily: theme.custom.text.mono } }}
            />
            <Autocomplete
              options={kycFields}
              getOptionLabel={(o) => o.name || ''}
              value={kycFields.find((f) => f.id === kycFieldId) || null}
              onChange={(_, v) => setKycFieldId(v?.id || null)}
              renderInput={(params) => <MuiTextField {...params} label={t('resources.default-mapping.fields.kycFields.name')} />}
            />
            {!hidePriority && (
              <MuiTextField
                label={t('resources.default-mapping.fields.priority')}
                type="number"
                value={priority}
                onChange={(e) => setPriority(parseInt(e.target.value, 10) || 0)}
                fullWidth
                inputProps={{ min: 0 }}
                helperText={t('screens.defaultMappings.priorityHelp')}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setEditOpen(false)} disabled={saving}>{t('ra.action.cancel')}</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !standardFieldName.trim() || !kycFieldId}>
            {t('ra.action.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth onClick={(e) => e.stopPropagation()}>
        <DialogTitle>{t('ra.message.delete_title', { name: record?.standardFieldName || '', id: '' })}</DialogTitle>
        <DialogContent><Typography>{t('ra.message.delete_content')}</Typography></DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setDeleteOpen(false)} disabled={saving}>{t('ra.action.cancel')}</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={saving}>{t('ra.action.delete')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

/* ── Help ── */
const DefaultMappingHelp: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const t = useTranslate();
  return (
    <HelpDialog open={open} onClose={onClose} title={t('default-mapping.help_title')}>
      <Typography variant="body2">{t('default-mapping.help_intro')}</Typography>
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{t('default-mapping.help_naming_title')}</Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          {SOURCES.map((source) => (
            <Typography component="li" variant="body2" key={source}>
              <Mono>{source}</Mono> — {t(`default-mapping.help_${source.toLowerCase()}`)}
            </Typography>
          ))}
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary">{t('default-mapping.help_example')}</Typography>
      <Typography variant="body2">{t('screens.defaultMappings.helpQr')}</Typography>
    </HelpDialog>
  );
};

/* ── The two tables: fields the OCR engine competes over, then QR-only fields ── */
const SplitContent: React.FC<{ kycFields: KycFieldOption[] }> = ({ kycFields }) => {
  const t = useTranslate();
  const listContext = useListContext<DefaultMappingRecord>();
  if (listContext.isPending) return null;
  const records = listContext.data ?? [];
  const ocrRecords = records.filter((r) => !isQrField(r));
  const qrRecords = records.filter(isQrField);
  // One list, two tables: each gets the list's context (sort, refresh) with its own rows.
  const subList = (data: DefaultMappingRecord[]) =>
    ({ ...listContext, data, total: data.length }) as ListControllerResult<DefaultMappingRecord>;

  const actions = (hidePriority?: boolean) => (r: DefaultMappingRecord) => (
    <DefaultMappingActions record={r} kycFields={kycFields} hidePriority={hidePriority} />
  );
  const standardField = (r: DefaultMappingRecord) => <Mono>{r.standardFieldName}</Mono>;
  const qrField = (r: DefaultMappingRecord) => <Mono>{(r.kycFields?.name || '').replace(/^QR_/, '')}</Mono>;

  return (
    <Box sx={{ minHeight: 0, overflow: { sm: 'auto' } }}>
      <ListContextProvider value={subList(ocrRecords)}>
        <ResponsiveDatagrid<DefaultMappingRecord>
          rowClick={false}
          mobile={{
            primary: standardField,
            lines: [
              { label: t('resources.default-mapping.fields.kycFields.name'), render: (r) => <KycFieldCell record={r} /> },
              { label: t('resources.default-mapping.fields.priority'), render: (r) => <InlinePriority record={r} /> },
            ],
            actions: actions(),
          }}
        >
          <FunctionField sortBy="standardFieldName" label={t('resources.default-mapping.fields.standardFieldName')} render={standardField} />
          <FunctionField
            sortable={false}
            label={t('resources.default-mapping.fields.kycFields.name')}
            render={(r: DefaultMappingRecord) => <KycFieldCell record={r} />}
          />
          <FunctionField
            sortBy="priority"
            label={t('resources.default-mapping.fields.priority')}
            render={(r: DefaultMappingRecord) => <InlinePriority record={r} />}
          />
          <FunctionField label="" textAlign="right" sortable={false} render={actions()} />
        </ResponsiveDatagrid>
      </ListContextProvider>

      {qrRecords.length > 0 && (
        <Stack spacing={1.5} sx={{ pt: 3 }}>
          <Box sx={{ px: { sm: 2 } }}>
            <Typography variant="h3" component="h2">
              {t('screens.defaultMappings.qrTitle', { count: qrRecords.length })}
            </Typography>
            <Alert severity="info" sx={{ mt: 1 }}>{t('screens.defaultMappings.qrInfo')}</Alert>
          </Box>
          <ListContextProvider value={subList(qrRecords)}>
            <ResponsiveDatagrid<DefaultMappingRecord>
              rowClick={false}
              mobile={{
                primary: standardField,
                lines: [{ label: t('screens.defaultMappings.qrField'), render: qrField }],
                actions: actions(true),
              }}
            >
              <FunctionField sortable={false} label={t('resources.default-mapping.fields.standardFieldName')} render={standardField} />
              <FunctionField sortable={false} label={t('screens.defaultMappings.qrField')} render={qrField} />
              <FunctionField label="" textAlign="right" sortable={false} render={actions(true)} />
            </ResponsiveDatagrid>
          </ListContextProvider>
        </Stack>
      )}
    </Box>
  );
};

const parseImport = (data: unknown, format: FileFormat): DefaultMappingItem[] | null => {
  const toItem = (r: Record<string, unknown>): DefaultMappingItem => ({
    standardFieldName: String(r.standardFieldName ?? '').trim(),
    kycFieldName: String(r.kycFieldName ?? '').trim(),
    priority: Number(r.priority) || 0,
  });
  if (format === 'json') {
    // Either the export's `{ items: [...] }` payload or a bare array.
    const raw = Array.isArray(data) ? data : (data as { items?: unknown })?.items;
    return Array.isArray(raw) ? raw.map(toItem) : null;
  }
  return Array.isArray(data) ? data.map(toItem) : null;
};

/** Rendered inside the list's context so the filter badge counts the active filters. */
const Header: React.FC<{ filtersOpen: boolean; onToggleFilters: () => void }> = ({ filtersOpen, onToggleFilters }) => {
  const t = useTranslate();
  const navigate = useNavigate();
  const notify = useNotify();
  const activeCount = useActiveFilterCount();
  const { permissions } = usePermissions<string>();
  const isAdmin = permissions === USER_ROLES.ADMIN;
  const [helpOpen, setHelpOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const importFlow = useImportFlow({ endpoint: '/admin/default-mapping/Import', parse: parseImport });

  const handleExport = async (format: ExportFormat) => {
    try {
      const r = await httpClient.get<{ items?: DefaultMappingItem[] }>('/admin/default-mapping/Export');
      const payload = r.data || {};
      const items = payload.items || [];
      const filename = `default-mappings-${new Date().toISOString().slice(0, 10)}`;
      if (format === 'csv') {
        downloadFile(items as unknown as Record<string, unknown>[], filename, 'csv');
      } else {
        downloadFile(payload as unknown as Record<string, unknown>[], filename, 'json');
      }
      notify(t('screens.defaultMappings.exported', { smart_count: items.length }), { type: 'success' });
    } catch {
      notify('screens.defaultMappings.exportFailed', { type: 'error' });
    }
  };

  return (
    <>
      <SectionHeader
        left={
          <PageTitle
            title={t('resources.default-mapping.name', { smart_count: 2 })}
            subtitle={t('screens.defaultMappings.subtitle')}
          />
        }
        help={{ label: t('screens.defaultMappings.help'), onClick: () => setHelpOpen(true) }}
        filters={<FilterToggle open={filtersOpen} onToggle={onToggleFilters} activeCount={activeCount} />}
        export={isAdmin ? { label: t('ra.action.export'), onClick: () => setExportOpen(true) } : undefined}
        actions={isAdmin ? [{ label: t('common.import'), onClick: importFlow.start, disabled: importFlow.importing }] : undefined}
        create={{ label: t('common.create'), onClick: () => navigate('/default-mapping/create') }}
      />
      <DefaultMappingHelp open={helpOpen} onClose={() => setHelpOpen(false)} />
      <ExportFormatDialog open={exportOpen} onClose={() => setExportOpen(false)} onExport={handleExport} />
      {importFlow.dialog}
    </>
  );
};

export const DefaultMappingList = () => {
  const t = useTranslate();
  const [kycFields, setKycFields] = useState<KycFieldOption[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    httpClient.get<KycFieldOption[]>('/admin/kyc-field/GetAllKYCFields').then((r) => setKycFields(r.data));
  }, []);

  const filters = [
    <TextInput key="q" source="q" label={t('ra.action.search')} alwaysOn resettable />,
    ...(filtersOpen
      ? [
          <SelectInput
            key="source"
            source="StandardFieldName"
            label={t('default-mapping.source')}
            choices={SOURCES.map((id) => ({ id, name: t(`screens.defaultMappings.sources.${id}`) }))}
            alwaysOn
            emptyText={t('common.filters.all')}
          />,
        ]
      : []),
  ];

  return (
    <ListBase perPage={100} sort={{ field: 'priority', order: 'ASC' }}>
      <ListScreen header={<Header filtersOpen={filtersOpen} onToggleFilters={() => setFiltersOpen((o) => !o)} />}>
        <ListView filters={filters} actions={false} empty={false}>
          <SplitContent kycFields={kycFields} />
        </ListView>
      </ListScreen>
    </ListBase>
  );
};
